import { useSetFlowTo, normalizePath } from 'Simple/Flow.js'
import { useMutation } from 'Data/Api.js'
import { useDataValue, useDataChange } from 'Simple/Data.js'
import {
  notifyError,
  notifySuccess,
  useNotifications,
} from 'Logic/Notifications.js'
import { PAYMENT_ACCOUNT_TYPES } from 'Data/common-constants.js'

import mutationCreateInvoice from './mutation-create-invoice.graphql.js'
import mutationPersonPaymentAccount from './mutation-person-payment-account.graphql.js'
import mutationInsurancePaymentAccount from './mutation-insurance-payment-account.graphql.js'

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data) {
  let [, notify] = useNotifications()
  let current_location_id = useDataValue({
    viewPath: props.viewPath,
    context: 'global',
    path: 'current_location.id',
  })
  let parent_organization_id = useDataValue({
    context: 'global',
    path: 'current_location.parent_company.id',
    viewPath: props.viewPath,
  })
  let refreshEmbeddedSection = useDataChange({
    context: 'global',
    path: 'refresh_embedded_section',
    viewPath: props.viewPath,
  })

  let [, executeMutationCreateInvoice] = useMutation(mutationCreateInvoice)
  let [, executeMutationPersonPaymentAccount] = useMutation(
    mutationPersonPaymentAccount
  )
  let [, executeMutationInsurancePaymentAccount] = useMutation(
    mutationInsurancePaymentAccount
  )
  let setFlowTo = useSetFlowTo(props.viewPath)

  return async function onSubmit({ value, originalValue, args, change }) {
    if (!value.payment_account || !value.treatment_id) {
      return true
    }
    let retail_items = value.retail_items.filter(item => item.type_id)
    if (!value.charges.length && !retail_items.length) {
      notify(
        notifyError(
          'Please add at least one charge or retail item to the invoice'
        )
      )
      return
    }

    let payment_account_id = value.payment_account.payment_account_id

    // we have to create new payment account
    if (!payment_account_id) {
      switch (value.payment_account.account_type) {
        case PAYMENT_ACCOUNT_TYPES.Person: {
          // this will trigger refetch on AccountSelect
          let mutationResponse = await executeMutationPersonPaymentAccount({
            payer_person_id: value.payment_account.payer_person.id,
            parent_organization_id,
          })

          if (mutationResponse.error) throw mutationResponse.error

          payment_account_id =
            mutationResponse.data.insert_vaxiom_person_payment_accounts_one.id
          break
        }
        case PAYMENT_ACCOUNT_TYPES.Insurance: {
          // this will trigger refetch on AccountSelect
          let mutationResponse = await executeMutationInsurancePaymentAccount({
            insurance_company_id: value.payment_account.insurance_company_id,
            parent_organization_id,
          })

          if (mutationResponse.error) throw mutationResponse.error

          payment_account_id =
            mutationResponse.data.insert_vaxiom_insurance_payment_accounts_one
              .id
          break
        }
        default: {
          throw new Error(
            `Invalid payment account type ${value.payment_account.account_type}`
          )
        }
      }

      // at this point, new payment account has been created, and AccountSelect data is being refetched
      // setting selected_in AccountSelect correctly position this new payment account into `Existing` and select it in dropdown
      // this is only necessary for case when the XyzPayment mutation fails, its kind of a recovery mechanism for this form
      change(next => {
        next.default_payment_account_id = payment_account_id
      })
    }

    let mutationResponse = await executeMutationCreateInvoice({
      invoice: {
        patient_id: value.patient_id,
        payment_account_id,
        treatment_id: value.treatment_id,
        insured_id: value.payment_account.insured_id,
        due_date: value.due_date,
        notes: value.notes,
        // remove retail item added by default
        retail_items,
        charges: value.charges,
      },
      current_location_id,
    })

    if (mutationResponse.error) {
      notify(notifyError('Failed to create invoice'))
      return
    }

    notify(notifySuccess('Invoice created successfully'))

    setFlowTo(normalizePath(props.viewPath, '../No'))
    // TODO: imho, this is bad ux, user will loose filters, toggles in core ui etc...
    refreshEmbeddedSection(Date.now())
  }
}
